import { useCallback, useEffect, useState } from "react";
import { Coins, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errText, API } from "@/lib/api";
import { NativeSelect } from "@/components/NativeSelect";
import { REWARD_TYPES } from "@/components/RewardCard";
import { formatDate } from "@/lib/constants";

const EMPTY = { title: "", description: "", cost: "", reward_type: "promo", delivery_method: "", codes: "", valid_until: "", activation: "" };

function FilePick({ label, accept, file, onPick, testid }) {
  return (
    <label className="flex items-center justify-between gap-2 border border-dashed border-[#c8e6d3] rounded-lg px-3 py-2 cursor-pointer hover:bg-[#e8f5ed] text-sm">
      <span className="text-[#1f5f42] font-semibold">{label}</span>
      <span className="text-xs text-[#6b8a7b] truncate max-w-[150px]">{file ? file.name : "Выбрать"}</span>
      <input type="file" accept={accept} className="hidden" onChange={(e) => onPick(e.target.files?.[0] || null)} data-testid={testid} />
    </label>
  );
}

function Redemptions() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/redemptions").then((r) => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="rounded-2xl bg-white border border-[#d1e7dd] divide-y divide-[#eef6f1] mt-5" data-testid="admin-redemptions-list">
      <div className="p-4 font-display font-bold text-[#0b2e20]">История обменов</div>
      {items.length === 0 && <p className="p-6 text-center text-sm text-[#6b8a7b]">Обменов пока нет</p>}
      {items.map((d) => (
        <div key={d.id} className="p-4 text-sm flex flex-wrap gap-x-3 gap-y-1" data-testid={`admin-redemption-${d.id}`}>
          <span className="font-semibold text-[#1f5f42]">{d.username}</span><span>→ {d.title}</span>
          {d.code && <code className="font-bold">{d.code}</code>}
          <span className="text-[#6b8a7b]">{d.cost} коинов · {formatDate(d.created_at)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminRewards() {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [cover, setCover] = useState(null);
  const [cert, setCert] = useState(null);
  const [stamp, setStamp] = useState(Date.now());
  const load = useCallback(() => { api.get("/admin/rewards").then((r) => { setItems(r.data); setStamp(Date.now()); }).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, cost: Number(f.cost), codes: f.codes.split("\n"), valid_until: f.valid_until || null };
    try {
      const { data } = editId ? await api.put(`/admin/rewards/${editId}`, body) : await api.post("/admin/rewards", body);
      for (const [slot, file] of [["cover", cover], ["certificate", cert]]) {
        if (!file) continue;
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/rewards/${data.id}/file/${slot}`, fd);
      }
      toast.success(editId ? "Награда обновлена" : "Награда добавлена");
      setF(EMPTY); setEditId(null); setCover(null); setCert(null); load();
    } catch (err) { toast.error(errText(err)); load(); }
  };
  const edit = (r) => { setEditId(r.id); setCover(null); setCert(null); setF({ title: r.title, description: r.description, cost: String(r.cost), reward_type: r.reward_type, delivery_method: r.delivery_method || "", codes: (r.codes || []).join("\n"), valid_until: r.valid_until || "", activation: r.activation || "" }); };
  const remove = async (r) => {
    if (!window.confirm(`Удалить «${r.title}»?`)) return;
    try { await api.delete(`/admin/rewards/${r.id}`); toast.success("Удалено"); load(); }
    catch (err) { toast.error(errText(err)); }
  };

  return (
    <div>
    <div className="grid grid-cols-1 lg:grid-cols-[400px_minmax(0,1fr)] gap-5">
      <form onSubmit={submit} className="rounded-2xl bg-white border border-[#d1e7dd] p-5 space-y-3 h-fit" data-testid="reward-form">
        <h3 className="font-display font-bold text-lg text-[#0b2e20]">{editId ? "Изменить награду" : "Новая награда"}</h3>
        <Input required minLength={2} placeholder="Название" value={f.title} onChange={set("title")} data-testid="reward-title-input" />
        <Textarea rows={3} placeholder="Описание" value={f.description} onChange={set("description")} data-testid="reward-description-input" />
        <Input required type="number" min={1} placeholder="Стоимость в коинах" value={f.cost} onChange={set("cost")} data-testid="reward-cost-input" />
        <NativeSelect value={f.reward_type} onChange={set("reward_type")} data-testid="reward-type-select">
          {Object.entries(REWARD_TYPES).map(([k, v]) => <option key={k} value={k}>{v.name}</option>)}
        </NativeSelect>
        <Input placeholder="Способ получения (например: введи промокод на сайте ...)" value={f.delivery_method} onChange={set("delivery_method")} data-testid="reward-delivery-input" />
        {f.reward_type === "promo" && (
          <Textarea rows={4} placeholder={"Промокоды — по одному на строку.\nКаждый выдаётся один раз."} value={f.codes} onChange={set("codes")} className="font-mono text-sm" data-testid="reward-codes-input" />
        )}
        <Textarea rows={3} placeholder={"Как активировать (видно после обмена). Ссылки станут кликабельными"} value={f.activation} onChange={set("activation")} maxLength={2000} data-testid="reward-activation-input" />
        <label className="block text-sm">
          <span className="text-[#1f5f42] font-semibold">Срок действия (до)</span>
          <Input type="date" value={f.valid_until} onChange={set("valid_until")} className="mt-1" data-testid="reward-valid-until-input" />
          <span className="text-xs text-[#6b8a7b]">Пусто — бессрочно</span>
        </label>
        <FilePick label="Обложка" accept=".jpg,.jpeg,.png,.webp,.gif" file={cover} onPick={setCover} testid="reward-cover-input" />
        {f.reward_type === "certificate" && <FilePick label="Сертификат (картинка/pdf)" accept=".jpg,.jpeg,.png,.webp,.gif,.pdf" file={cert} onPick={setCert} testid="reward-certificate-input" />}
        <div className="flex gap-2">
          <Button type="submit" className="rounded-full bg-[#1f5f42] hover:bg-[#174731]" data-testid="reward-submit-button">{editId ? "Сохранить" : "Добавить"}</Button>
          {editId && <Button type="button" variant="outline" className="rounded-full" onClick={() => { setEditId(null); setF(EMPTY); setCover(null); setCert(null); }} data-testid="reward-cancel-edit-button">Отмена</Button>}
        </div>
      </form>
      <div className="space-y-3" data-testid="admin-rewards-list">
        {items.length === 0 && <p className="text-sm text-[#6b8a7b] py-10 text-center">Наград пока нет</p>}
        {items.map((r) => (
          <div key={r.id} className="rounded-xl bg-white border border-[#d1e7dd] p-4 flex items-center gap-4" data-testid={`admin-reward-${r.id}`}>
            <div className="h-14 w-20 shrink-0 rounded-lg overflow-hidden bg-[#e8f5ed]">
              {r.has_cover && <img src={`${API}/rewards/${r.id}/cover?t=${stamp}`} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-[#0b2e20]">{r.title} <span className="text-xs font-semibold text-[#2e7d56]">· {REWARD_TYPES[r.reward_type]?.name}</span></div>
              {r.description && <div className="text-sm text-[#4b6b5c] truncate">{r.description}</div>}
              <div className="text-xs text-[#6b8a7b] mt-1" data-testid={`admin-reward-info-${r.id}`}>
                {r.reward_type === "promo" && `Промокодов осталось: ${r.stock}`}
                {r.reward_type === "certificate" && (r.has_certificate ? "Сертификат загружен" : "Сертификат не загружен")}
                {r.reward_type === "gift" && (r.delivery_method || "Подарок")}
              </div>
            </div>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/30 font-bold text-sm"><Coins className="h-3.5 w-3.5" />{r.cost}</span>
            <button onClick={() => edit(r)} className="h-8 w-8 grid place-items-center rounded-full text-[#1f5f42] hover:bg-[#e8f5ed]" data-testid={`edit-reward-${r.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(r)} className="h-8 w-8 grid place-items-center rounded-full text-red-600 hover:bg-red-50" data-testid={`delete-reward-${r.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
    <Redemptions />
    </div>
  );
}
